/**
 * SetupPopout — the right-docked "Set up your genie" panel for the immersive
 * live-test setup screen. Onboarding-kit style: only the two first-run buckets
 * (Identity & brand, Train) as click-to-open rows + one expanded Voice block.
 * Operational/advanced settings are flagged behind a single Advanced link.
 *
 * SELF-SAVING: owns its own edit state + useUpdateAgent. The footer button
 * persists greeting, web message, language, voice and personality traits in one
 * deep-merged update. Complex rows open the real platform editors (which save
 * themselves) or deep-link to the genie hub.
 *
 * Co-located with ImmersiveSetupPage (prefix Setup*). Brand tokens only.
 */
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check, ChevronDown, ChevronRight, Loader2, Play } from "lucide-react";
import { SetupRow, SectionHeader } from "@/components/voice-genie/atoms";
import BrandingModal from "@/components/branding/BrandingModal";
import { KnowledgeDrawer } from "@/components/agent/drawers/KnowledgeDrawer";
import GenieInsightsDrawer from "@/components/agent/drawers/GenieInsightsDrawer";
import GeniePromptDrawer from "@/components/agent/drawers/GeniePromptDrawer";
import PronunciationModal from "@/components/agent/modals/configuration/PronunciationModal";
import { useUpdateAgent } from "@/hooks/agent/useAgents";
import { useVoices } from "@/hooks/voice/useVoices";
import { LANGUAGES } from "@/utils/config/elevenlabs";
import { getGenieBrandTheme, withAlpha } from "@/utils/branding/genieBrandTheme";
// Personality traits we surface as chips. `on` is inferred from the system
// prompt at render time (substring match) and toggling rewrites the prompt.
// Shared with the /welcome onboarding step so both write traits the same way.
import {
  TRAIT_OPTIONS,
  buildTraitLine,
  stripTraitLine,
  type TraitLabel,
} from "@/lib/agent/personalityTraits";
import type { Agent } from "@/types/agent/agent";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface SetupPopoutProps {
  agent?: Agent;
  isLoading?: boolean;
}

export const SetupPopout = ({ agent, isLoading }: SetupPopoutProps) => {
  const navigate = useNavigate();
  const updateAgent = useUpdateAgent();

  const cc = agent?.settings?.conversation_config;
  const promptText = cc?.agent?.prompt?.prompt ?? "";
  const theme = getGenieBrandTheme(agent);

  // ── Real voices ──
  const { data: voicesData } = useVoices({ pageSize: 100 });
  const voices = voicesData?.voices ?? [];

  // ── Section open state ──
  const [voiceOpen, setVoiceOpen] = useState(true);
  const [personalityOpen, setPersonalityOpen] = useState(true);

  // ── Editor open state ──
  const [brandOpen, setBrandOpen] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const [kbOpen, setKbOpen] = useState(false);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [pronunciationOpen, setPronunciationOpen] = useState(false);

  // ── Inline edit state (essentials) ──
  const [greeting, setGreeting] = useState(cc?.agent?.first_message ?? "");
  const [webMessage, setWebMessage] = useState(agent?.welcome_message ?? "");
  const [languageCode, setLanguageCode] = useState(cc?.agent?.language ?? "en");
  const [voiceId, setVoiceId] = useState(cc?.tts?.voice_id ?? "");
  const [traits, setTraits] = useState<TraitLabel[]>(() =>
    TRAIT_OPTIONS.filter((t) => promptText.toLowerCase().includes(t.toLowerCase())),
  );

  // ── Voice preview (real preview_url playback) ──
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);

  const selectedVoice = useMemo(
    () => voices.find((v) => v.id === voiceId),
    [voices, voiceId],
  );
  const selectedVoiceName = selectedVoice?.name ?? agent?.voice_label ?? "Your genie's voice";

  const playPreview = (previewUrl?: string, id?: string) => {
    if (!previewUrl) {
      toast("No preview available for this voice yet");
      return;
    }
    if (audioRef.current) {
      audioRef.current.pause();
    }
    const audio = new Audio(previewUrl);
    audioRef.current = audio;
    setPlayingVoiceId(id ?? null);
    audio.onended = () => setPlayingVoiceId(null);
    audio.play().catch(() => setPlayingVoiceId(null));
  };

  const selectVoice = (id: string, previewUrl?: string) => {
    setVoiceId(id);
    playPreview(previewUrl, id);
  };

  const toggleTrait = (label: TraitLabel) =>
    setTraits((prev) =>
      prev.includes(label) ? prev.filter((t) => t !== label) : [...prev, label],
    );

  const knowledgeCount = agent?.knowledge_base?.length ?? 0;
  const knowledgeValue = knowledgeCount
    ? `${knowledgeCount} ${knowledgeCount === 1 ? "source" : "sources"}`
    : "Add sources to train your genie";

  // ── Real self-save (deep-merge nested settings) ──
  const handleSave = async () => {
    if (!agent) {
      toast.error("This genie isn't ready to save yet");
      return;
    }

    const basePrompt = stripTraitLine(promptText);
    const traitLine = buildTraitLine(traits);
    const newPrompt = traitLine ? `${basePrompt}\n${traitLine}`.trim() : basePrompt;

    try {
      await updateAgent.mutateAsync({
        agent,
        updateData: {
          welcome_message: webMessage,
          settings: {
            ...agent.settings,
            conversation_config: {
              ...cc,
              agent: {
                ...cc?.agent,
                first_message: greeting,
                language: languageCode,
                prompt: {
                  ...cc?.agent?.prompt,
                  prompt: newPrompt,
                },
              },
              tts: {
                ...cc?.tts,
                voice_id: voiceId,
              },
            },
          },
        },
      });
      toast.success("Your genie is saved");
    } catch {
      toast.error("We couldn't save your genie. Please try again.");
    }
  };

  // ── Loading skeleton (matches the popout layout) ──
  if (isLoading) {
    return (
      <aside className="w-full md:w-[560px] md:xl:w-[680px] md:2xl:w-[760px] flex-shrink-0 flex-1 min-h-0 md:h-full bg-white md:border-l border-gray-200 md:shadow-[-18px_0_40px_-24px_rgba(15,23,42,0.25)] flex flex-col">
        <div className="px-7 pt-6 pb-5 border-b border-[#F3F4F6]">
          <div className="h-5 w-40 rounded-md bg-gray-100 animate-pulse" />
          <div className="h-3 w-full rounded bg-gray-100 animate-pulse mt-3" />
          <div className="h-3 w-3/4 rounded bg-gray-100 animate-pulse mt-2" />
        </div>
        <div className="flex-1 px-7 pt-5 space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-[58px] rounded-xl border border-gray-200 bg-gray-50 animate-pulse" />
          ))}
        </div>
        <div className="p-6 border-t border-[#F3F4F6]">
          <div className="h-12 rounded-2xl bg-gray-100 animate-pulse" />
        </div>
      </aside>
    );
  }

  return (
    <aside
      data-hg-block="settings-popout"
      className="w-full md:w-[560px] md:xl:w-[680px] md:2xl:w-[760px] flex-shrink-0 flex-1 min-h-0 md:h-full bg-white md:border-l border-gray-200 md:shadow-[-18px_0_40px_-24px_rgba(15,23,42,0.25)] flex flex-col"
    >
      {/* Header */}
      <div
        style={{ backgroundImage: `linear-gradient(to bottom, ${withAlpha(theme.primary, "0a")}, transparent)` }}
        className="px-7 pt-6 pb-5 border-b border-[#F3F4F6]"
      >
        <div className="flex items-center gap-2.5">
          <div
            style={{ background: theme.gradient, boxShadow: `0 1px 2px 0 ${withAlpha(theme.primary, "4d")}` }}
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          >
            <GenieIcon name="sparkles" size={18} color="#FFFFFF" />
          </div>
          <h2 className="flex-1 text-lg font-bold text-[#222B45] tracking-tight">
            Set up your genie
          </h2>
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </div>
        <p className="text-[13px] text-gray-500 mt-2.5 leading-relaxed max-w-[46ch]">
          Your genie's already drafted. Review the essentials and tweak anything you like, then
          save. Your changes apply on the next call. Everything else is one tap away under Advanced.
        </p>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 min-h-0 overflow-y-auto px-7 pt-5 pb-6 flex flex-col gap-3">
        {/* ─── IDENTITY & BRAND ─── */}
        <SectionHeader label="Identity & brand" />

        {/* Brand kit — opens the real branding editor */}
        <button
          type="button"
          onClick={() => (agent ? setBrandOpen(true) : navigate("/genie-hub/my-genies"))}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 bg-white hover:bg-genie-background transition-colors text-left"
        >
          <div style={{ background: theme.gradient }} className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0">
            <GenieIcon name="palette" size={17} color="#FFFFFF" />
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-sm font-semibold text-[#222B45]">Brand kit</span>
            <span className="block text-xs text-gray-500">Logo, colours &amp; avatar</span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <span style={{ backgroundColor: theme.primary }} className="w-4 h-4 rounded-full border border-black/5" />
            <span style={{ backgroundColor: theme.secondary }} className="w-4 h-4 rounded-full border border-black/5" />
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        </button>

        {/* Greeting — inline editable */}
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: withAlpha(theme.secondary, "1a") }}
            >
              <GenieIcon name="microphone" size={18} color={theme.secondary} />
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-sm font-semibold text-[#222B45] block">Greeting</span>
              <span className="text-xs text-gray-500">The first thing callers hear</span>
            </div>
          </div>
          <textarea
            value={greeting}
            onChange={(e) => setGreeting(e.target.value)}
            rows={2}
            placeholder="Kia ora, you've reached…"
            className="w-full resize-none rounded-lg border border-gray-200 bg-genie-background px-3 py-2 text-[13px] text-[#222B45] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-genie-primary/30 focus:border-genie-primary/40"
          />
        </div>

        {/* Web message — inline editable */}
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: withAlpha(theme.secondary, "1a") }}
            >
              <GenieIcon name="chat" size={18} color={theme.secondary} />
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-sm font-semibold text-[#222B45] block">Web message</span>
              <span className="text-xs text-gray-500">Opening line on your genie's page</span>
            </div>
          </div>
          <textarea
            value={webMessage}
            onChange={(e) => setWebMessage(e.target.value)}
            rows={2}
            placeholder="Hi! Ask me anything…"
            className="w-full resize-none rounded-lg border border-gray-200 bg-genie-background px-3 py-2 text-[13px] text-[#222B45] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-genie-primary/30 focus:border-genie-primary/40"
          />
        </div>

        {/* Voice — expanded hero block (real voices + preview) */}
        <div
          data-hg-block="setup-voice"
          style={{ borderColor: withAlpha(theme.primary, "59"), boxShadow: `0 0 0 3px ${withAlpha(theme.primary, "0f")}` }}
          className="rounded-xl border bg-white overflow-hidden"
        >
          <button
            type="button"
            onClick={() => setVoiceOpen((v) => !v)}
            className="w-full flex items-center gap-3 p-3 border-b border-[#F3F4F6] text-left"
          >
            <div style={{ backgroundColor: withAlpha(theme.primary, "1a") }} className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0">
              <GenieIcon name="play" size={17} color={theme.primary} />
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-sm font-semibold text-[#222B45]">Voice</span>
              <p className="text-xs text-gray-500 truncate">{selectedVoiceName}</p>
            </div>
            <ChevronDown
              style={{ color: theme.primary }}
              className={`w-4 h-4 transition-transform ${voiceOpen ? "" : "-rotate-90"}`}
            />
          </button>

          {voiceOpen && (
            <div className="p-3 flex flex-col gap-2">
              <p className="text-xs text-[#7C8B9C]">Not quite right? Tap to hear another.</p>
              {voices.length === 0 ? (
                <div className="flex items-center gap-2 px-2 py-4 text-xs text-[#7C8B9C]">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Loading voices…
                </div>
              ) : (
                voices.slice(0, 8).map((v) => {
                  const selected = v.id === voiceId;
                  const isPlaying = v.id === playingVoiceId;
                  const tagParts = [
                    v.labels?.descriptive,
                    v.labels?.gender,
                    v.labels?.accent,
                  ].filter(Boolean);
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => selectVoice(v.id, v.preview_url)}
                      style={selected ? { backgroundColor: withAlpha(theme.primary, "14"), borderColor: withAlpha(theme.primary, "66") } : undefined}
                      className={`h-[46px] pl-2 pr-3 rounded-[10px] flex items-center gap-2.5 transition-colors text-left border ${
                        selected ? "" : "bg-white border-gray-200 hover:bg-genie-background"
                      }`}
                    >
                      <span
                        style={selected ? { backgroundColor: theme.primary } : undefined}
                        className={`w-[30px] h-[30px] rounded-full flex items-center justify-center flex-shrink-0 ${
                          selected ? "" : "bg-[#F1F5F9]"
                        }`}
                      >
                        {isPlaying ? (
                          <Loader2
                            className={`w-3 h-3 animate-spin ${selected ? "text-white" : "text-[#64748B]"}`}
                          />
                        ) : (
                          <Play
                            className={`w-3 h-3 ${selected ? "text-white" : "text-[#64748B]"}`}
                            fill="currentColor"
                          />
                        )}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-[13px] font-semibold text-[#222B45]">{v.name}</span>
                        <span className="block text-[11.5px] text-gray-500 truncate capitalize">
                          {tagParts.join(" · ") || "Voice"}
                        </span>
                      </span>
                      {selected ? (
                        <span className="flex items-center gap-1.5 flex-shrink-0">
                          <span style={{ backgroundColor: theme.primary, boxShadow: `0 0 0 3px ${withAlpha(theme.primary, "2e")}` }} className="w-1.5 h-1.5 rounded-full" />
                          <span style={{ color: theme.primary }} className="text-xs font-semibold">Selected</span>
                        </span>
                      ) : (
                        <span className="text-xs font-semibold text-[#7C8B9C] flex-shrink-0">Use</span>
                      )}
                    </button>
                  );
                })
              )}
              <button
                type="button"
                onClick={() => navigate("/genie-hub/voice-playground")}
                style={{ color: theme.primary }}
                className="flex items-center gap-1.5 mt-0.5 text-xs font-semibold hover:underline"
              >
                More voices · Voice playground
                <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>

        {/* Personality — trait chips (inferred from prompt, rewritten on save) */}
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <button
            type="button"
            onClick={() => setPersonalityOpen((v) => !v)}
            className="w-full flex items-center gap-3 p-3 border-b border-[#F3F4F6] text-left"
          >
            <div style={{ backgroundColor: withAlpha(theme.secondary, "1a") }} className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0">
              <GenieIcon name="user" size={17} color={theme.secondary} />
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-sm font-semibold text-[#222B45]">Personality</span>
              <p className="text-xs text-gray-500">How your genie acts</p>
            </div>
            <ChevronDown
              className={`w-4 h-4 text-gray-400 transition-transform ${personalityOpen ? "" : "-rotate-90"}`}
            />
          </button>
          {personalityOpen && (
            <div className="p-3 flex flex-wrap gap-2">
              {TRAIT_OPTIONS.map((label) => {
                const on = traits.includes(label);
                return (
                  <button
                    key={label}
                    type="button"
                    onClick={() => toggleTrait(label)}
                    style={on ? { backgroundColor: withAlpha(theme.secondary, "1f"), borderColor: withAlpha(theme.secondary, "59"), color: theme.secondary } : undefined}
                    className={`h-[30px] px-3 rounded-full flex items-center gap-1.5 text-[12.5px] font-semibold transition-colors border ${
                      on ? "" : "bg-white border-gray-200 text-[#7C8B9C] hover:bg-genie-background"
                    }`}
                  >
                    {on && <Check className="w-3 h-3" />}
                    {label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Language — inline select */}
        <div className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 bg-white">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: "#7C8B9C1a" }}
          >
            <GenieIcon name="globe" size={18} color="#7C8B9C" />
          </div>
          <span className="flex-1 min-w-0 text-sm font-semibold text-[#222B45]">Language</span>
          <select
            value={languageCode}
            onChange={(e) => setLanguageCode(e.target.value)}
            className="max-w-[55%] rounded-lg border border-gray-200 bg-genie-background px-2.5 py-1.5 text-[13px] font-medium text-[#222B45] focus:outline-none focus:ring-2 focus:ring-genie-primary/30 focus:border-genie-primary/40"
            aria-label="Language"
          >
            {LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.flag ? `${l.flag} ` : ""}
                {l.name}
              </option>
            ))}
          </select>
        </div>

        {/* ─── TRAIN ─── */}
        <SectionHeader label="Train" className="mt-1" />

        {/* Instructions — opens the real system-prompt editor in the genie hub */}
        <button
          type="button"
          onClick={() => (agent ? setPromptOpen(true) : navigate("/genie-hub/my-genies"))}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 bg-white hover:bg-genie-background transition-colors text-left"
        >
          <div style={{ backgroundColor: withAlpha(theme.secondary, "1a") }} className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0">
            <GenieIcon name="sparkles" size={17} color={theme.secondary} />
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-sm font-semibold text-[#222B45] block">Instructions</span>
            <span className="text-xs text-gray-500 truncate block">
              Drafted from your details, refine if you like
            </span>
          </div>
          <span style={{ backgroundColor: withAlpha(theme.secondary, "1a"), color: theme.secondary }} className="px-2 h-5 rounded-full flex items-center text-[10.5px] font-bold flex-shrink-0">
            Auto
          </span>
          <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        </button>

        {/* Knowledge base — opens the real knowledge drawer */}
        <SetupRow
          genieIcon="book-open"
          tint={theme.primary}
          label="Knowledge base"
          value={knowledgeValue}
          onClick={() => (agent ? setKbOpen(true) : navigate("/genie-hub/my-genies"))}
        />

        {/* Insights — opens the real insights drawer */}
        <button
          type="button"
          onClick={() => (agent ? setInsightsOpen(true) : navigate("/genie-hub/my-genies"))}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 bg-white hover:bg-genie-background transition-colors text-left"
        >
          <div className="w-9 h-9 rounded-lg bg-genie-accent/10 flex items-center justify-center flex-shrink-0">
            <GenieIcon name="lightbulb" size={17} color="#FFBB33" />
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-sm font-semibold text-[#222B45] block">Insights</span>
            <span className="text-xs text-gray-500 block">Capture caller intent &amp; outcomes</span>
          </div>
          <span className="px-2 h-5 rounded-full bg-[#F1F5F9] flex items-center text-[10.5px] font-bold text-gray-500 flex-shrink-0">
            {agent?.insights ? "Set" : "Add"}
          </span>
          <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        </button>

        {/* Pronunciations — opens the real pronunciation editor */}
        <button
          type="button"
          onClick={() => (agent ? setPronunciationOpen(true) : navigate("/genie-hub/my-genies"))}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 bg-white hover:bg-genie-background transition-colors text-left"
        >
          <div className="w-9 h-9 rounded-lg bg-[#F1F5F9] flex items-center justify-center flex-shrink-0">
            <GenieIcon name="settings" size={17} color="#7C8B9C" />
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-sm font-semibold text-[#222B45] block">Pronunciations</span>
            <span className="text-xs text-gray-500 block">Teach names &amp; brand words</span>
          </div>
          <span className="px-2 h-5 rounded-full bg-[#F1F5F9] flex items-center text-[10.5px] font-bold text-gray-500 flex-shrink-0">
            {(agent?.settings?.pronunciations?.length ?? 0) > 0 ? "Set" : "Add"}
          </span>
          <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        </button>

        {/* ─── ADVANCED (flagged link) ─── */}
        <button
          type="button"
          onClick={() => navigate(agent ? `/genie-hub/my-genies/${agent.id}` : "/genie-hub/my-genies")}
          className="mt-1.5 w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-dashed border-gray-300 bg-genie-background hover:bg-white transition-colors text-left"
        >
          <div className="w-9 h-9 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
            <GenieIcon name="settings" size={17} color="#7C8B9C" />
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-sm font-semibold text-[#222B45] block">Advanced setup</span>
            <span className="text-xs text-gray-500 block leading-snug">
              Distribution, conversation behaviour, integrations, billing
            </span>
          </div>
          <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        </button>
      </div>

      {/* Footer save */}
      <div className="p-6 border-t border-[#F3F4F6]">
        <button
          type="button"
          onClick={handleSave}
          disabled={updateAgent.isPending || !agent}
          style={{ background: theme.gradient, boxShadow: `0 12px 24px -8px ${withAlpha(theme.primary, "4d")}` }}
          className="h-12 w-full rounded-2xl flex items-center justify-center gap-2 text-[15px] font-semibold text-white transition-opacity hover:opacity-95 disabled:opacity-60"
        >
          {updateAgent.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Check className="w-4 h-4" />
          )}
          {updateAgent.isPending ? "Saving…" : "Looks good, save genie"}
        </button>
      </div>

      {/* ─── Real editors (each owns its own save) ─── */}
      {agent && (
        <>
          <BrandingModal
            key={`${agent.id}-${brandOpen}`}
            genie={agent}
            isOpen={brandOpen}
            onClose={() => setBrandOpen(false)}
          />
          <GeniePromptDrawer genie={agent} isOpen={promptOpen} onClose={() => setPromptOpen(false)} />
          <KnowledgeDrawer genie={agent} isOpen={kbOpen} onClose={() => setKbOpen(false)} />
          <GenieInsightsDrawer
            genie={agent}
            isOpen={insightsOpen}
            onClose={() => setInsightsOpen(false)}
          />
          <PronunciationModal
            genie={agent}
            isOpen={pronunciationOpen}
            onClose={() => setPronunciationOpen(false)}
          />
        </>
      )}
    </aside>
  );
};

export default SetupPopout;
